import { useEffect, useState, useCallback, useRef, useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { debounce, get, isEmpty, isEqual } from "lodash";
import { v4 as uuid } from "uuid";
import { useDispatch, useSelector } from "react-redux";
import { Helmet } from "react-helmet-async";
import { selectSelectedSubTab } from "../../components/organisms/BreadCrumb";
import ServiceLeftSidebar from "./ServiceLeftSidebar";
import { actions } from "./dataModels.reducers";
import {
  selectSelectedServiceId,
  selectService,
  updateService,
  selectSelectedDataModel,
  fetchEndpoints,
  fetchServices,
} from "./index";
import ServiceRightSidebar from "./ServiceRightSidebar";
import ReactFlowEditor from "../../components/molecules/ReactFlowEditor/ReactFlowEditor";
import { actions as breadCrumbActions } from "../../components/organisms/BreadCrumb/breadCrumb.reducers";
import {
  ALERT_TIMEOUT,
  EMPTY_ARRAY,
  TABS,
  INDEX,
  ZERO,
  MAX_DRAWER_WIDTH,
  MIN_DRAWER_WIDTH,
} from "../../constants";
import { checkIsConnectionComplete } from "./services.helpers";
import { fetchMulesoftEndpoints } from "../Mulesoft";
import { FIELDS, ROOT_NODE } from "./datamodels.constants";
import { actions as mulesoftActions } from "../Mulesoft/mulesoft.reducers";
import classNames from "classnames";
import { renderNoDataModelLayout } from "../../helpers";
import Spinner, { SPINNER_NAMES } from "../../components/organisms/Spinner";

const {
  setSelectedServiceId,
  setSelectedDataModelId,
  setSelectedNode,
  setSelectedService,
} = actions;
const { setSelectedIntegrationId } = mulesoftActions;
const { setSelectedTab, setSelectedSubTab } = breadCrumbActions;
const { MULESOFT } = TABS;
const { SECOND } = INDEX;
const { LOGICAL, OUTPUTS, INPUTS } = FIELDS;
const { FETCH_SERVICE, FETCH_SERVICES } = SPINNER_NAMES;
const MAX_UNDO_LIMIT = 50;
const UNDO_DEBOUNCE_TIME_LIMIT = 150;
const DEFAULT_DRAWER_WIDTH = 300;
const SCREEN_WIDTH = 953;
const RIGHT_SIDEBAR_WIDTH_AT_SMALL_SCREEN = 268;

const Service = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { dataModelId, serviceId, integrationId } = useParams();
  const selectedServiceId = useSelector(selectSelectedServiceId);
  const selectedDataModel = useSelector(selectSelectedDataModel);
  const selectedService = useSelector(selectService);
  const selectedSubTab = useSelector(selectSelectedSubTab);
  const currentPath = document.location.pathname;
  const selectedSubTabRef = useRef(selectedSubTab);
  const [draggable, setShouldDraggable] = useState({
    rightSideBar: false,
    leftSideBar: false,
  });
  const [newNodePosition, setNewNodePosition] = useState({ x: 255, y: 15 });

  useEffect(() => {
    dispatch(setSelectedSubTab(selectedSubTabRef.current));
    return () => dispatch(setSelectedSubTab());
  }, [dispatch]);

  const [service, setService] = useState();
  const [isSaved, setIsSaved] = useState(false);
  const [isFlowValidated, setIsFlowValidated] = useState(null);
  const [updateSelectedNodeData, setUpdateSelectedNodeData] = useState(ZERO);
  const [isViewOnlyMode, setIsViewOnlyMode] = useState(false);
  const areUnsavedChanges = useRef(false);
  const showServiceUnsavedAlert = useRef(false);
  const serviceLeftSideBarRef = useRef(null);
  const serviceRightSideBarRef = useRef(null);
  const screenWidthRef = useRef(null);
  const [leftDrawerWidth, setLeftDrawerWidth] = useState(DEFAULT_DRAWER_WIDTH);
  const [rightDrawerWidth, setRightDrawerWidth] =
    useState(DEFAULT_DRAWER_WIDTH);
  const [showLeftSidebarButton, setShowLeftSidebarButton] = useState(false);
  const [showRightSidebarButton, setShowRightSidebarButton] = useState(false);

  let initialPosition;
  let initialClientX;

  useEffect(() => {
    if (leftDrawerWidth < MIN_DRAWER_WIDTH) {
      setLeftDrawerWidth(ZERO);
      setShowLeftSidebarButton(true);
    } else setShowLeftSidebarButton(false);

    if (rightDrawerWidth < MIN_DRAWER_WIDTH) {
      setRightDrawerWidth(ZERO);
      setShowRightSidebarButton(true);
    } else setShowRightSidebarButton(false);
  }, [leftDrawerWidth, rightDrawerWidth]);

  const handleMouseMoveLeftSidebar = (e) => {
    const widthChange = e.clientX - initialClientX;
    let newWidth = initialPosition + widthChange;
    newWidth = Math.max(0, Math.min(MAX_DRAWER_WIDTH, newWidth));
    setLeftDrawerWidth(newWidth);
  };

  const handleMouseUpLeftSidebar = () => {
    setShouldDraggable({ leftSideBar: false });
    window.removeEventListener("mousemove", handleMouseMoveLeftSidebar);
    window.removeEventListener("mouseup", handleMouseUpLeftSidebar);
  };

  const handleMouseDownLeftSidebar = (e) => {
    setShouldDraggable({ leftSideBar: true });
    initialPosition = leftDrawerWidth;
    initialClientX = e.clientX;
    window.addEventListener("mousemove", handleMouseMoveLeftSidebar);
    window.addEventListener("mouseup", handleMouseUpLeftSidebar);
  };

  const handleMouseDownRightSidebar = () => {
    setShouldDraggable({ rightSideBar: true });
    window.addEventListener("mousemove", handleMouseMoveRightSidebar);
    window.addEventListener("mouseup", handleMouseUpRightSidebar);
  };

  const handleMouseMoveRightSidebar = (e) => {
    setRightDrawerWidth(
      Math.min(MAX_DRAWER_WIDTH, window.innerWidth - e.clientX)
    );
  };

  const handleMouseUpRightSidebar = () => {
    setShouldDraggable({ rightSideBar: false });
    window.removeEventListener("mousemove", handleMouseMoveRightSidebar);
    window.removeEventListener("mouseup", handleMouseUpRightSidebar);
  };

  showServiceUnsavedAlert.current = () =>
    areUnsavedChanges.current &&
    window.confirm("You have unsaved changes, do you want to save them?") &&
    updateReactFlowStore();

  const [{ past, future }, setUndoState] = useState({
    past: [],
    present: {},
    future: [],
  });
  const [{ nodes, edges }, setFlow] = useState({ nodes: [], edges: [] });

  const reRenderReactFlow = debounce(
    useCallback(() => setUpdateSelectedNodeData((e) => ++e), []),
    100
  );

  const handleUndo = useCallback(() => {
    let pastFlow = past;
    const undoFlow = pastFlow.pop();
    setUndoState(({ present, future }) => ({
      past: pastFlow,
      present: undoFlow,
      future: [...future, present],
    }));
    setFlow(undoFlow);
    reRenderReactFlow();
  }, [past, reRenderReactFlow]);

  const handleRedo = useCallback(() => {
    let futureFlow = future;
    const undoFlow = futureFlow.pop();
    setUndoState((e) => ({
      past: [...e.past, e.present],
      present: undoFlow,
      future: futureFlow,
    }));
    setFlow(undoFlow);
    reRenderReactFlow();
  }, [future, reRenderReactFlow]);

  const updateUndoData = useMemo(
    () =>
      debounce((e) => {
        setUndoState(({ past, present }) => {
          let updatedPast = past;
          past.length === MAX_UNDO_LIMIT && updatedPast.pop();
          updatedPast = isEmpty(present.nodes) ? [] : [...past, present];
          return {
            past: updatedPast,
            present: e,
            future: [],
          };
        });
      }, UNDO_DEBOUNCE_TIME_LIMIT),
    []
  );

  const setElements = useCallback(
    (value) => {
      setFlow((e) => ({
        nodes: value.nodes || e.nodes,
        edges: value.edges || e.edges,
      }));
      if (!isEqual(value.nodes, nodes) || !isEqual(value.edges, edges))
        updateUndoData({
          nodes: value.nodes || nodes,
          edges: value.edges || edges,
        });
    },
    [nodes, edges, updateUndoData]
  );

  const getUpdatedNodes = useCallback(
    (nodesData) =>
      nodesData.map((node) => ({
        id: node.id,
        data: node.data,
        position: node.position,
        height: node.height,
        width: node.width,
      })),
    []
  );

  const getUpdatedEdges = useCallback(
    (edgesData) =>
      edgesData.map((edge) => ({
        id: edge.id,
        source: edge.source,
        sourceHandle: edge.sourceHandle,
        target: edge.target,
        targetHandle: edge.targetHandle,
      })),
    []
  );

  const checkAreUnsavedNodesAndEdges = useCallback(() => {
    const savedNodes = selectedService?.flow?.nodes || EMPTY_ARRAY;
    const savedEdges = selectedService?.flow?.edges || EMPTY_ARRAY;
    const areUnsaved = !(
      isEqual(getUpdatedNodes(savedNodes), getUpdatedNodes(nodes)) &&
      isEqual(getUpdatedEdges(savedEdges), getUpdatedEdges(edges))
    );
    return areUnsaved;
  }, [nodes, edges, selectedService, getUpdatedNodes, getUpdatedEdges]);

  useEffect(() => {
    areUnsavedChanges.current =
      selectedService && !isEmpty(nodes) && checkAreUnsavedNodesAndEdges();
  }, [nodes, selectedService, checkAreUnsavedNodesAndEdges]);

  useEffect(() => {
    if (selectedService) setService(selectedService);
  }, [dispatch, selectedService]);

  useEffect(() => {
    if (serviceId) dispatch(setSelectedServiceId(serviceId));
  }, [dispatch, serviceId]);

  useEffect(() => {
    dispatch(setSelectedDataModelId(dataModelId));
    dispatch(setSelectedIntegrationId(integrationId));
    return () => {
      dispatch(setSelectedServiceId(null));
      dispatch(setSelectedNode(null));
      dispatch(setSelectedService(null));
      if (integrationId)
        dispatch(fetchMulesoftEndpoints({ id: integrationId }));
      else {
        dispatch(
          fetchEndpoints({
            id: dataModelId,
          })
        );
        dispatch(
          fetchServices({
            id: dataModelId,
          })
        );
      }
    };
  }, [dataModelId, integrationId, dispatch]);

  useEffect(() => {
    const shouldSetCurrentServiceElements =
      selectedServiceId && service && !nodes.length;

    if (shouldSetCurrentServiceElements) {
      const nodesOfSelectedService = get(
        service,
        ["flow", "nodes"],
        EMPTY_ARRAY
      );
      const edgesOfSelectedService = get(
        service,
        ["flow", "edges"],
        EMPTY_ARRAY
      );
      setElements({
        nodes: nodesOfSelectedService,
        edges: edgesOfSelectedService,
      });
    }
  }, [nodes.length, selectedServiceId, service, setElements]);

  const checkValidation = useCallback((nodes, edges) => {
    const isConnectionValidate = nodes.every(
      (node) =>
        checkIsConnectionComplete(node, nodes, edges) &&
        get(node, ["data", "isValidated"])
    );
    setIsFlowValidated(isConnectionValidate);
    return isConnectionValidate;
  }, []);

  useEffect(() => {
    const urlArray = currentPath.toString().split("/");
    if (urlArray[SECOND] !== MULESOFT)
      dispatch(setSelectedTab(selectedDataModel));
  }, [dispatch, selectedDataModel, currentPath]);

  const updateServiceRequest = (value) =>
    dispatch(updateService(value)).then(() => {
      setIsSaved(true);
      setTimeout(() => {
        setIsSaved(false);
      }, ALERT_TIMEOUT);
    });

  const updateReactFlowStore = (updatedNodes = nodes, updatedEdges = edges) => {
    const flow = {
      nodes: updatedNodes,
      edges: updatedEdges,
    };
    if (selectedServiceId) {
      const isValid = checkValidation(updatedNodes, updatedEdges);
      updateServiceRequest({
        id: selectedServiceId,
        data: { flow, isValid },
      });
    }
  };

  const updateNode = useCallback((node, updatedEdges, handlesToBeDeleted) => {
    const updatedInputs = node.data.inputs.filter((input) => {
      const shouldRetainHandle =
        !input.custom ||
        updatedEdges.find(
          (edge) =>
            edge.target === node.id && edge.targetHandle === `tava_${input.key}`
        );
      if (!shouldRetainHandle && node.data.group === LOGICAL) {
        const outputHandle = node.data.outputs.filter((output) =>
          output.key.includes(input.key)
        );
        outputHandle.length &&
          outputHandle.forEach((handle) =>
            handlesToBeDeleted.push({
              nodeId: node.id,
              handleId: handle.key,
              type: OUTPUTS,
            })
          );
      }
      return shouldRetainHandle;
    });
    return {
      ...node,
      data: {
        ...node.data,
        inputs: updatedInputs,
      },
    };
  }, []);

  const removeHandle = useCallback(
    ({
      nodeId,
      handleId,
      type,
      updatedNodes = nodes,
      updatedEdges = edges,
      shouldSetElements,
    }) => {
      const handlesToBeDeleted = [];

      updatedEdges = updatedEdges.filter((edge) =>
        type === OUTPUTS
          ? edge.sourceHandle !== `tava_${handleId}`
          : edge.targetHandle !== `tava_${handleId}`
      );

      updatedNodes = updatedNodes.map((node) => {
        if (node.id === nodeId) {
          const updatedHandles = node.data[type].filter(
            (data) => data.key !== handleId
          );
          if (node.data.group === LOGICAL && type === INPUTS) {
            const outputHandle = node.data.outputs.filter((output) =>
              output.key.includes(handleId)
            );
            outputHandle.length &&
              outputHandle.forEach((handle) =>
                handlesToBeDeleted.push({
                  nodeId: node.id,
                  handleId: handle.key,
                  type: OUTPUTS,
                })
              );
          }
          return { ...node, data: { ...node.data, [type]: updatedHandles } };
        } else if (type === OUTPUTS)
          return updateNode(node, updatedEdges, handlesToBeDeleted);
        else return node;
      });

      let elements = { updatedNodes, updatedEdges };

      if (handlesToBeDeleted.length) {
        elements = handlesToBeDeleted.reduce(
          (updatedElements, handle) =>
            removeHandle({ ...handle, ...updatedElements }),
          elements
        );
      }
      if (shouldSetElements)
        setElements({
          nodes: elements.updatedNodes,
          edges: elements.updatedEdges,
        });
      else return elements;
    },
    [nodes, edges, setElements, updateNode]
  );

  // useEffect(() => {
  //   const handleBeforeUnload = (event) => {
  //     if (areUnsavedChanges.current) {
  //       event.preventDefault();
  //       event.returnValue = EMPTY_STRING;
  //     }
  //   };

  //   window.addEventListener("beforeunload", handleBeforeUnload);

  //   return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  // }, []);

  // useEffect(() => {
  //   // return () => showServiceUnsavedAlert.current();
  // }, [navigate]);

  const isDraggable = Object.values(draggable).some((value) => value);

  const handleMouseMove = (event) => {
    const cursorStyle = isDraggable ? "col-resize" : "grab";
    event.currentTarget.style.cursor = cursorStyle;
  };

  useEffect(() => {
    const updateLeftDrawerWidth = () => {
      const isSmallScreen = screenWidthRef.current.offsetWidth <= SCREEN_WIDTH;
      setLeftDrawerWidth(isSmallScreen ? ZERO : DEFAULT_DRAWER_WIDTH);
      setRightDrawerWidth(
        isSmallScreen
          ? RIGHT_SIDEBAR_WIDTH_AT_SMALL_SCREEN
          : DEFAULT_DRAWER_WIDTH
      );
    };
    window.addEventListener("resize", updateLeftDrawerWidth);
    return () => window.removeEventListener("resize", updateLeftDrawerWidth);
  }, []);

  const onDoubleClick = (nodeData) => {
    if (isEmpty(nodeData)) return;
    const { card } = nodeData;
    const id = uuid();

    const newNode = {
      ...ROOT_NODE,
      id,
      position: newNodePosition,
      deletable: true,
      zIndex: card.isParent ? -10 : 0,
      data: {
        ...card,
        id,
        isValidated: !!card.isValidated,
      },
    };
    setNewNodePosition({
      x: newNodePosition.x + 5,
      y: newNodePosition.y + 5,
    });
    setElements({
      nodes: [...nodes, newNode],
    });
  };

  return (
    <div
      className={classNames(
        "select-none relative flex flex-auto content overflow-hidden bg-gray-800 shadow-xl",
        { "!cursor-col-resize": isDraggable }
      )}
      ref={screenWidthRef}
    >
      <Helmet>
        <title>Service | TavaJS</title>
      </Helmet>
      {!selectedDataModel && !integrationId ? (
        <div className="p-6 flex-1">
          <Spinner name={[FETCH_SERVICE, FETCH_SERVICES]}>
            {renderNoDataModelLayout(navigate)}
          </Spinner>
        </div>
      ) : (
        <>
          {!isViewOnlyMode && (
            <div className="flex">
              <div className="overflow-y-scroll no-scrollbar">
                <div
                  ref={serviceLeftSideBarRef}
                  className="transition-all duration-300"
                  style={{ width: leftDrawerWidth }}
                >
                  <ServiceLeftSidebar
                    draggable={draggable.leftSideBar}
                    onDoubleClick={onDoubleClick}
                  />
                </div>
              </div>
              <div
                className={classNames(
                  "cursor-col-resize h-full w-[2px] hover:scale-[1.3] hover:bg-blue-400",
                  {
                    " bg-blue-400": draggable.leftSideBar,
                  }
                )}
                onMouseDown={handleMouseDownLeftSidebar}
                onMouseUp={handleMouseUpLeftSidebar}
              />
            </div>
          )}
          <div className="overflow-y-scroll no-scrollbar flex-auto w-2/4 bg-white border border-gray-700">
            <div className="relative h-full">
              <ReactFlowEditor
                nodes={nodes}
                edges={edges}
                setElements={setElements}
                undo={handleUndo}
                redo={handleRedo}
                canRedo={future.length}
                canUndo={past.length}
                isFlowValidated={isFlowValidated}
                checkValidation={checkValidation}
                removeHandle={removeHandle}
                updateNode={updateNode}
                showSpinner={false}
                isViewOnlyMode={isViewOnlyMode}
                setIsViewOnlyMode={setIsViewOnlyMode}
                setShowLeftSidebarButton={setShowLeftSidebarButton}
                showLeftSidebarButton={showLeftSidebarButton}
                setRightDrawerWidth={setRightDrawerWidth}
                setLeftDrawerWidth={setLeftDrawerWidth}
                showRightSidebarButton={showRightSidebarButton}
                setShowRightSidebarButton={setShowRightSidebarButton}
                handleMouseMove={handleMouseMove}
              />
            </div>
          </div>
          <div
            className={classNames(
              "cursor-col-resize h-full w-[2px] hover:scale-[1.3] hover:bg-blue-400",
              {
                "bg-blue-400": draggable.rightSideBar,
              }
            )}
            onMouseDown={handleMouseDownRightSidebar}
            onMouseUp={handleMouseUpRightSidebar}
          />
          <div className="flex ">
            <div className="overflow-y-scroll no-scrollbar flex justify-center">
              <div
                ref={serviceRightSideBarRef}
                className="transition-all duration-300"
                style={{ width: rightDrawerWidth }}
              >
                <ServiceRightSidebar
                  updateReactFlowStore={updateReactFlowStore}
                  isSaved={isSaved}
                  nodes={nodes}
                  edges={edges}
                  setElements={setElements}
                  removeHandle={removeHandle}
                  updateSelectedNodeData={updateSelectedNodeData}
                  isViewOnlyMode={isViewOnlyMode}
                  draggable={draggable.rightSideBar}
                />
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default Service;
